/**
 * Here so mqtt lib works on the browser
 * @see {https://github.com/mqttjs/MQTT.js/issues/1474}
 * @see {https://github.com/mqttjs/MQTT.js/pull/1497}
 */
import 'core-js/modules/web.immediate'
import type { Mutable } from 'utility-types'
import mqtt, { type MqttClient } from 'mqtt'

import { assert, BulkEvent, delay, Droppable, isArrayNotEmpty, Queue, type ReadonlyDate } from '../../../utils'

import { ConnectwareError, ConnectwareErrorType, isTopicSubscriptionResponseSuccessful, type TopicSubscriptionResponse } from '../../../domain'

import type { TopFrameWrapper } from '../../workers'
import { MqttTopic } from '../Topic'
import { parsePayload } from './PayloadParser'
import type { MqttMessageFromWorker, MqttMessageToWorker, SerializableMqttTopicMessageEvent } from './Types'

const sharedTopicPattern = /^\$share\/[^/]+\/[^/]+$/

type ActionType<T extends MqttMessageToWorker | MqttMessageFromWorker, A extends string> = T extends { action: A } ? Omit<T, 'action'> : never

class ConnectedClient {
    private readonly droppable = new Droppable()

    private readonly subscriptionQueue = new Queue()

    private readonly client: Pick<MqttClient, 'on' | 'once' | 'subscribe' | 'unsubscribe' | 'removeAllListeners' | 'end' | 'connected'>

    private previousTopics: readonly string[] = []

    constructor (private readonly args: ActionType<MqttMessageToWorker, 'connect'>) {
        this.client = mqtt.connect(args.url, { username: args.username, password: args.password, connectTimeout: args.timeout })
    }

    onError (handler: (e: Error) => void): void {
        this.client.on('error', (e) => {
            /**
             * The code below ignores errors that happen during reconnection
             * And only pass it along if the client is still disconnected
             */
            this.onLoad((connected) => {
                if (!connected) {
                    handler(e)
                }
            })
        })
    }

    onLoad (handler: (connected: boolean) => void): void {
        void Promise.race([
            /** Connection promise */
            new Promise<boolean>((resolve) => this.client.once('connect', () => resolve(this.client.connected))),
            /** Time-out promise */
            delay(this.args.timeout).then(() => false),
        ]).then(handler)
    }

    /**
     * This function should only call the handler after the reconnection is succesful
     * not at every reconnection attempt
     */
    onReconnect (handler: VoidFunction): void {
        let lastRecon: number | null = null

        this.client.on('reconnect', () => (lastRecon = Date.now()))
        this.client.on('connect', () => {
            const lastCon = Date.now()

            if (
                /** No reconnection was started */
                lastRecon === null ||
                /** Reconnection was too long ago so something else is up */
                lastRecon + this.args.timeout < lastCon ||
                /** Client is not connected */
                !this.client.connected
            ) {
                return
            }

            handler()
        })
    }

    onBulk (handler: (batch: readonly (readonly [topics: string, payload: Buffer])[]) => void): void {
        const bulkEvent = new BulkEvent<readonly [topics: string, payload: Buffer]>(this.args.cycle, true)

        /** Listen to messages once connected */
        this.client.on('message', (topic, payload) => bulkEvent.append([topic, payload]))

        /** Every cycle, send this up the chain */
        this.droppable.onDrop(bulkEvent.onCycled((batch) => handler(batch)))
    }

    disconnect (): void {
        this.client.removeAllListeners()
        this.client.end()
        this.droppable.drop()
    }

    subscribe (topics: readonly string[]): Promise<TopicSubscriptionResponse> {
        /** Wait for the last subscription flow to be done and then start the next one */
        return this.subscriptionQueue.push(async () => {
            const { previousTopics } = this

            /** Unsubscribe from previous topics if there are any */
            if (isArrayNotEmpty(previousTopics)) {
                this.client.unsubscribe([...previousTopics])
            }

            const validTopics: Mutable<typeof topics> = []
            let nonSubscribed: Pick<TopicSubscriptionResponse, 'invalid' | 'shared'> = { invalid: [], shared: [] }
            let subscriptionResult: Pick<TopicSubscriptionResponse, 'subscribed' | 'noPermissions' | 'unknown'> = {
                subscribed: [],
                noPermissions: [],
                unknown: [],
            }

            /** Strip out topics that are either malformed or too deep */
            topics.forEach((topic) => {
                if (!new MqttTopic(topic, this.args.maxTopicDepth).valid) {
                    nonSubscribed = { ...nonSubscribed, invalid: [...nonSubscribed.invalid, topic] }
                } else if (sharedTopicPattern.test(topic)) {
                    nonSubscribed = { ...nonSubscribed, shared: [...nonSubscribed.shared, topic] }
                } else {
                    validTopics.push(topic)
                }
            })

            /** Subscribe to new topics if there are any and validate subscription was successful */
            subscriptionResult = isArrayNotEmpty(validTopics)
                ? await new Promise((resolve) =>
                      this.client.subscribe([...validTopics], (error, granted, packet) => {
                          assert(granted, new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Granted array is always sent if the topics are valid'))
                          resolve(
                              granted.reduce((r, { topic }, k) => {
                                  const qos = packet?.granted[k]

                                  if (qos === 128) {
                                      /** QOS with this value, means missing permissions */
                                      return { ...r, noPermissions: [...r.noPermissions, topic] }
                                  }

                                  if (!error && qos === 0) {
                                      /** There are no issues, so just subscribe */
                                      return { ...r, subscribed: [...r.subscribed, topic] }
                                  }

                                  /** There were issues with the subscription, so mark everyone */
                                  return { ...r, unknown: [...r.unknown, topic] }
                              }, subscriptionResult)
                          )
                      })
                  )
                : subscriptionResult

            this.previousTopics = subscriptionResult.subscribed

            return { ...nonSubscribed, ...subscriptionResult }
        })
    }
}

/**
 * This webworker is here in order to prevent
 * the massive amount of mqtt topic messages
 * to overwhelm the UI event loop
 *
 * It will collect whatever messages it receives in an ammount of time
 * And trigger one single event with all collected messages
 *
 * It depends on two things
 *  - The internal connection to mqtt
 *  - The parcel plugin that deals with wrapping this code to be put inside the web worker
 */
export class MqttWorker {
    private client: ConnectedClient | null = null

    private topics: readonly string[] = []

    constructor (private readonly worker: TopFrameWrapper<MqttMessageFromWorker, MqttMessageToWorker>) {}

    private withClient (withClient: (client: ConnectedClient) => Promise<void> | void, withoutClient: VoidFunction = this.sendStateError): void {
        if (this.client) {
            void withClient(this.client)
        } else {
            withoutClient()
        }
    }

    private send (message: MqttMessageFromWorker): void {
        this.withClient(() => this.worker.send(message))
    }

    private readonly sendStateError = (): void => {
        this.worker.send({ action: 'error', type: 'state', message: "Worker's state is not properly setup" })
    }

    private connect (args: ActionType<MqttMessageToWorker, 'connect'>): void {
        this.withClient(this.sendStateError, () => {
            const client = (this.client = new ConnectedClient(args))

            /** Handle errors */
            client.onError((e) => {
                this.send({ action: 'error', type: 'genericError', message: e.message })
                this.disconnect()
            })

            /** Wait for load */
            client.onLoad((connected) => {
                /** Make sure connection is made with-in the accepted timeframe */
                if (!connected) {
                    this.send({ action: 'error', type: 'couldNotConnect', message: 'Could not connect to the mqtt client' })
                    return
                }

                client.onBulk((batch) => {
                    const topicalBatch = batch.reduce<SerializableMqttTopicMessageEvent[]>((r, [topic, payload]) => {
                        if (!isArrayNotEmpty(this.topics)) {
                            return r
                        }

                        const sources = new MqttTopic(topic, null).filterSources(this.topics)
                        const [serializedPayload, possibleTypes] = parsePayload(payload)

                        if (isArrayNotEmpty(sources)) {
                            r.push({ topic, possibleTypes, payload: serializedPayload, timestamp: this.getCurrentTime(), sources })
                        }

                        return r
                    }, [])

                    if (isArrayNotEmpty(topicalBatch)) {
                        this.send({ action: 'messages', batch: topicalBatch, topics: this.topics })
                    }
                })
            })

            /** Detect reconnections and re-subscribe to all topics */
            client.onReconnect(() => {
                this.applySubscription(this.topics, (response) => {
                    if (!isTopicSubscriptionResponseSuccessful(response)) {
                        /** Had issues resubscribing, should never occur in real life scenarios, but this covers all basis */
                        this.send({ action: 'error', type: 'couldNotReconnect', message: 'There was an issue completing reconnection' })
                    }
                })
            })
        })
    }

    private disconnect (): void {
        this.withClient((client) => client.disconnect())
        this.client = null
    }

    /**
     * Actually performs the subscription, handles exceptions and updates internal controls
     */
    private applySubscription (topics: readonly string[], handler: (response: TopicSubscriptionResponse) => void): void {
        this.withClient(async (client) => {
            const response = await client.subscribe(topics)
            this.topics = response.subscribed
            handler(response)
        })
    }

    /**
     * Subscribes and signals top frame what subscription was actually made
     */
    private subscribe ({ id, topics }: ActionType<MqttMessageToWorker, 'subscribe'>): void {
        this.applySubscription(topics, (response) => this.send({ id, action: 'subscription', response }))
    }

    /** This is exposed to enable tests mocks */
    // eslint-disable-next-line class-methods-use-this
    protected getCurrentTime (): ReadonlyDate {
        return new Date()
    }

    start (): void {
        this.worker.listen((data) => {
            switch (data.action) {
                case 'connect':
                    this.connect(data)
                    break
                case 'disconnect':
                    this.disconnect()
                    break
                case 'subscribe':
                    this.subscribe(data)
                    break
            }
        })
    }
}
